
import gsap from 'gsap'
import ScrollTrigger from 'gsap/ScrollTrigger'
import g from '../globals/variables'
import { debounce } from 'throttle-debounce'

type DrawRange = {
  start: number,
  end: number,
}

const RecipesLines = {
  init(): void {
    if (!document.querySelector('.recipes-line')) {
      return
    }

    gsap.registerPlugin(ScrollTrigger)

    document.querySelectorAll('.recipes-line').forEach((line: HTMLElement) => {
      const isCenterLine = line.classList.contains('recipes-line--to-center')
      const isCenterToSidesLine = line.classList.contains('recipes-line--to-sides')
      const isFooterLine = line.classList.contains('recipes-line--footer')
      const isSideLine = line.classList.contains('recipes-line--to-left') || line.classList.contains('recipes-line--to-right')

      line.querySelector('path').setAttribute('vector-effect', 'non-scaling-stroke')
      // line.querySelector('svg').setAttribute('preserveAspectRatio', 'none')

      isCenterLine && this.handleCenterLine(line)
      isCenterToSidesLine && this.handleCenterToSidesLine(line)
      isFooterLine && this.handleFooterLine(line)
      isSideLine && this.handleSideLine(line)
    })

    if (document.querySelector<HTMLElement>('.recipes-category__cards')) {
      this.placeCardGridLines(document.querySelectorAll<HTMLElement>('.recipes-category__cards .recipe-card'))
      window.addEventListener('resize', debounce(100, () => {
        this.placeCardGridLines(document.querySelectorAll<HTMLElement>('.recipes-category__cards .recipe-card'))
      }))
    }

    if (document.querySelector<HTMLElement>('.recipes-brochures__cards')) {
      this.placeCardGridLines(document.querySelectorAll<HTMLElement>('.recipes-brochures__cards .brochure-card'))
      window.addEventListener('resize', debounce(100, () => {
        this.placeCardGridLines(document.querySelectorAll<HTMLElement>('.recipes-brochures__cards .brochure-card'))
      }))
    }
  },

  handleCenterLine(line: HTMLElement): void {
    line.insertAdjacentHTML('beforeend', line.querySelector('svg').outerHTML)

    line.querySelectorAll('svg').forEach((svg, index) => {
      const path = svg.querySelector<SVGPathElement>('path')

      this.setDrawState(path, index === 0 ? 0 : 100, index === 0 ? 0 : 100)

      ScrollTrigger.create({
        animation: this.createDrawAnimation(path, {
          end: index === 0 ? 50 : 100,
          start: index === 0 ? 0 : 50,
        }),
        end: '50% 50%',
        scrub: 3.4567,
        start: '50% 100%',
        trigger: line,
      })
    })
  },

  handleCenterToSidesLine(line: HTMLElement): void {
    line.insertAdjacentHTML('beforeend', line.querySelector('svg').outerHTML)
  
    line.querySelectorAll('svg').forEach((svg, index) => {
      const path = svg.querySelector<SVGPathElement>('path')

      this.setDrawState(path, 50, 50)
  
      ScrollTrigger.create({
        animation: this.createDrawAnimation(path, {
          end: index === 0 ? 50 : 100,
          start: index === 0 ? 0 : 50,
        }),
        start: '50% 100%',
        end: '50% 50%',  
        scrub: 3.4567,
        trigger: line,
      })
    })
  },

  handleFooterLine(line: HTMLElement): void {
    gsap.matchMedia().add(g.breakpoints, (context) => {
      const is = context.conditions
      const path = line.querySelector<SVGPathElement>('path')
      
      this.setDrawState(path, 0, 0)

      ScrollTrigger.create({
        animation: this.createDrawAnimation(path, { start: 0, end: 100 }),
        end: is.mobile ? '0% 30%' : '100% 100%',
        scrub: 3.4567,
        start: is.mobile ? '0% 90%' : '60% 100%',
        trigger: '.footer',
      })
    })
  },

  handleSideLine(line: HTMLElement): void {
    const isLeftToRight = line.classList.contains('recipes-line--to-right')
    const isRightToLeft = line.classList.contains('recipes-line--to-left')
    const path = line.querySelector<SVGPathElement>('path')

    this.setDrawState(path, isRightToLeft ? 100 : 0, isLeftToRight ? 0 : 100)

    ScrollTrigger.create({
      animation: this.createDrawAnimation(path, { start: 0, end: 100 }),
      end: '50% 50%',
      scrub: 3.4567,
      start: '50% 100%',
      trigger: line,
    })
  },

  createDrawAnimation(path: SVGPathElement, targetRange: DrawRange) {
    const currentRange = {
      end: parseFloat(path.dataset.drawEnd || '0'),
      start: parseFloat(path.dataset.drawStart || '0'),
    }

    return gsap.to(currentRange, {
      ...targetRange,
      ease: 'none',
      onUpdate: () => {
        this.setDrawState(path, currentRange.start, currentRange.end)
      },
    })
  },

  setDrawState(path: SVGPathElement, startPercent: number, endPercent: number): void {
    const length = path.getTotalLength()
    const clampedStart = gsap.utils.clamp(0, 100, startPercent)
    const clampedEnd = gsap.utils.clamp(0, 100, endPercent)
    const drawPadding = Math.max(length * 0.002, 2)
    const visibleLength = Math.max(Math.abs(clampedEnd - clampedStart) / 100 * length, 0.01)
    const paddedVisibleLength = Math.min(visibleLength + drawPadding, length + drawPadding)
    const dashOffset = -(Math.min(clampedStart, clampedEnd) / 100) * length - (drawPadding / 2)

    path.dataset.drawStart = clampedStart.toString()
    path.dataset.drawEnd = clampedEnd.toString()

    gsap.set(path, {
      overwrite: true,
      strokeDasharray: `${paddedVisibleLength} ${length + drawPadding}`,
      strokeDashoffset: dashOffset,
    })
  },

  placeCardGridLines(cards): void {    
    gsap.matchMedia().add(g.breakpoints, (context) => {
      const is = context.conditions
      
      if (cards.length >= 8) {
        gsap.set('.recipes-line--1', {
          top: is.desktop ? cards[7].offsetTop + 50 : is.tablet ? cards[5].offsetTop + 100 : cards[1].offsetTop + 150,
        })
      }

      if (cards.length >= 20) {
        gsap.set('.recipes-line--5', {
          top: is.desktop ? cards[19].offsetTop + 50 : is.tablet ? cards[17].offsetTop + 100 : cards[13].offsetTop + 150,
        })
      }
    })
  },
}

export default RecipesLines
